import {
  IInbox,
  IInboxMetadata,
  InboxType,
} from "@/shared/services/interface/responses/IInboxResponses";
import { TFunction } from "i18next";
import {
  ArrowRight,
  Box,
  Compass,
  Plus,
  SquareCheck,
  Trash,
} from "lucide-react";

export interface IInboxListPartial {
  new: IInbox[];
  today: IInbox[];
  yesterday: IInbox[];
  old: IInbox[];
}

export const TextAllFirstLetterToUpperCase = (text: string) => {
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const GetInboxList = (inboxList: IInbox[]): IInboxListPartial => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const todayInboxList = inboxList.filter((inbox) => {
    const inboxDate = new Date(inbox.updatedAt || inbox.createdAt);
    return (
      inboxDate.getFullYear() === today.getFullYear() &&
      inboxDate.getMonth() === today.getMonth() &&
      inboxDate.getDate() === today.getDate()
    );
  });

  const newList = todayInboxList.filter((inbox) => !inbox.isRead);

  const todayList = todayInboxList.filter((inbox) => inbox.isRead);

  const yesterdayList = inboxList.filter((inbox) => {
    const inboxDate = new Date(inbox.updatedAt || inbox.createdAt);
    return (
      inboxDate.getFullYear() === yesterday.getFullYear() &&
      inboxDate.getMonth() === yesterday.getMonth() &&
      inboxDate.getDate() === yesterday.getDate()
    );
  });

  const oldList = inboxList.filter(
    (inbox) =>
      new Date(inbox.updatedAt || inbox.createdAt) <
      new Date(yesterday.toDateString())
  );

  return {
    new: newList,
    today: todayList,
    yesterday: yesterdayList,
    old: oldList,
  };
};

const GetInboxListIcon = (type: InboxType) => {
  switch (type) {
    case InboxType.SPECIFICATION_INVITE:
      return {
        mainIcon: <SquareCheck className="!w-4 !h-4" />,
        subIcon: <Plus className="!w-4 !h-4" />,
      };
    case InboxType.SPECIFICATION_EXPLORE_MATCHED_PRODUCT_COMPLETED:
      return {
        mainIcon: <Compass className="!w-4 !h-4" />,
        subIcon: <ArrowRight className="!w-4 !h-4" />,
      };
    case InboxType.SPECIFICATION_EXPLORE_USER_AND_COMMENT_COMPLETED:
      return {
        mainIcon: <Compass className="!w-4 !h-4" />,
        subIcon: <ArrowRight className="!w-4 !h-4" />,
      };
    case InboxType.SPECIFICATION_EXPLORE_LOCAL_PARTNER_SUPPORT_COMPLETED:
      return {
        mainIcon: <Compass className="!w-4 !h-4" />,
        subIcon: <ArrowRight className="!w-4 !h-4" />,
      };
    case InboxType.SPECIFICATION_EXPLORE_COMPETITIVE_INDEX_COMPLETED:
      return {
        mainIcon: <Compass className="!w-4 !h-4" />,
        subIcon: <ArrowRight className="!w-4 !h-4" />,
      };
    case InboxType.SPECIFICATION_EXPLORE_PRODUCER_SCORE_COMPLETED:
      return {
        mainIcon: <Compass className="!w-4 !h-4" />,
        subIcon: <ArrowRight className="!w-4 !h-4" />,
      };
    default:
      return {
        mainIcon: <SquareCheck className="!w-4 !h-4" />,
        subIcon: <Plus className="!w-4 !h-4" />,
      };
  }
};

const GetInboxListTexts = (
  type: InboxType,
  metadata: IInboxMetadata,
  t: TFunction,
  isSender: boolean = false
) => {
  switch (type) {
    case InboxType.SPECIFICATION_INVITE:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:SPECIFICATION_INVITE", {
          name: metadata?.sendedBy?.email || "",
        }),
        subDescription: "",
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE_STARTED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:SUPPLIER_ANSWER_DESCRIPTION", {
          productName: TextAllFirstLetterToUpperCase(
            metadata?.product?.name || ""
          ),
        }),
        subDescription: isSender
          ? t("inbox:SUPPLIER_SPECIFICATION_INVITE_STARTED_SENDER", {
              name: metadata?.sendedBy?.email || "",
            })
          : t("inbox:SUPPLIER_SPECIFICATION_INVITE_STARTED_RECEIVER", {
              name: metadata?.sendedBy?.email || "",
            }),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:SUPPLIER_ANSWER_DESCRIPTION", {
          productName: TextAllFirstLetterToUpperCase(
            metadata?.product.name || ""
          ),
        }),
        subDescription: isSender
          ? t("inbox:SUPPLIER_SPECIFICATION_INVITE_SENDER", {
              name: metadata?.receivedBy?.email || "",
            })
          : t("inbox:SUPPLIER_SPECIFICATION_INVITE_RECEIVER", {
              name: metadata?.sendedBy?.email || "",
            }),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:SUPPLIER_ANSWER_DESCRIPTION", {
          productName: TextAllFirstLetterToUpperCase(
            metadata?.product?.name || ""
          ),
        }),
        subDescription: isSender
          ? t("inbox:SUPPLIER_SPECIFICATION_INVITE_COMPLETED_SENDER", {
              name: metadata?.sendedBy?.email || "",
            })
          : t("inbox:SUPPLIER_SPECIFICATION_INVITE_COMPLETED_RECEIVER", {
              name: metadata?.sendedBy?.email || "",
            }),
      };
    case InboxType.SPECIFICATION_EXPLORE_MATCHED_PRODUCT_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            metadata?.specification?.name || ""
          ),
        }),
        subDescription: t("inbox:EXPLORE_SUB_DESCRIPTION", {
          count: metadata?.itemCount || 150,
        }),
      };
    case InboxType.SPECIFICATION_EXPLORE_COMPETITIVE_INDEX_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            metadata?.specification?.name || ""
          ),
        }),
        subDescription: t("inbox:EXPLORE_SUB_DESCRIPTION", {
          count: metadata?.itemCount || 10,
        }),
      };
    case InboxType.SPECIFICATION_EXPLORE_LOCAL_PARTNER_SUPPORT_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            metadata?.specification?.name || ""
          ),
        }),
        subDescription: t("inbox:EXPLORE_SUB_DESCRIPTION", {
          count: metadata?.itemCount || 15,
        }),
      };
    case InboxType.SPECIFICATION_EXPLORE_PRODUCER_SCORE_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            metadata?.specification?.name || ""
          ),
        }),
        subDescription: t("inbox:EXPLORE_SUB_DESCRIPTION", {
          count: metadata?.itemCount || 6,
        }),
      };
    case InboxType.SPECIFICATION_EXPLORE_USER_AND_COMMENT_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            metadata?.specification?.name || ""
          ),
        }),
        subDescription: t("inbox:EXPLORE_SUB_DESCRIPTION", {
          count: metadata?.itemCount || 20,
        }),
      };

    default:
      return {
        title: "",
        description: "",
        subDescription: "",
      };
  }
};

export const GetExploreCompletedCount = (status: InboxType) => {
  switch (status) {
    case InboxType.SPECIFICATION_EXPLORE_MATCHED_PRODUCT_COMPLETED:
      return 1;
    case InboxType.SPECIFICATION_EXPLORE_USER_AND_COMMENT_COMPLETED:
      return 2;
    case InboxType.SPECIFICATION_EXPLORE_LOCAL_PARTNER_SUPPORT_COMPLETED:
      return 3;
    case InboxType.SPECIFICATION_EXPLORE_COMPETITIVE_INDEX_COMPLETED:
      return 4;
    case InboxType.SPECIFICATION_EXPLORE_PRODUCER_SCORE_COMPLETED:
      return 5;
    default:
      return 1;
  }
};

const GetInboxDisplayTexts = (
  type: InboxType,
  metadata: IInboxMetadata,
  t: TFunction,
  isSender: boolean = false
) => {
  switch (type) {
    case InboxType.SPECIFICATION_INVITE:
      return {
        title: t("inbox:SPECIFICATION_INVITE_DETAIL", {
          name: metadata?.specification?.name || "",
        }),
        description: t("inbox:SPECIFICATION_INVITE_SUB_DETAIL", {
          userName: metadata?.user?.email || "",
          specificationName: metadata?.specification?.name || "",
        }),
        subDescription: t("inbox:SPECIFICATION_INVITE_DETAIL", {
          name: metadata?.user?.email || "",
        }),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE:
      return {
        title: metadata?.specification?.name || "",
        description: isSender
          ? t("inbox:SUPPLIER_INVITE_TEXT_SENDER", {
              name: metadata?.receivedBy?.email || "",
              specificationName: metadata?.specification?.name || "",
            })
          : t("inbox:SUPPLIER_INVITE_TEXT_RECEIVER", {
              name: metadata?.sendedBy?.email || "",
              specificationName: metadata?.specification?.name || "",
            }),
        subDescription: "",
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE_STARTED:
      return {
        title: metadata?.specification?.name || "",
        description: isSender
          ? t("inbox:SUPPLIER_SPECIFICATION_INVITE_STARTED_SENDER", {
              name: metadata?.receivedBy?.email || "",
            })
          : t("inbox:SUPPLIER_SPECIFICATION_INVITE_STARTED_RECEIVER", {
              name: metadata?.sendedBy?.email || "",
            }),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
        description: isSender
          ? t("inbox:SUPPLIER_SPECIFICATION_INVITE_COMPLETED_SENDER", {
              name: metadata?.receivedBy?.email || "",
            })
          : t("inbox:SUPPLIER_SPECIFICATION_INVITE_COMPLETED_RECEIVER", {
              name: metadata?.sendedBy?.email || "",
            }),
      };
    case InboxType.SPECIFICATION_EXPLORE_MATCHED_PRODUCT_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
      };
    case InboxType.SPECIFICATION_EXPLORE_COMPETITIVE_INDEX_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
      };
    case InboxType.SPECIFICATION_EXPLORE_LOCAL_PARTNER_SUPPORT_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
      };
    case InboxType.SPECIFICATION_EXPLORE_PRODUCER_SCORE_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
      };
    case InboxType.SPECIFICATION_EXPLORE_USER_AND_COMMENT_COMPLETED:
      return {
        title: metadata?.specification?.name || "",
      };
    default:
      return {
        title: "",
        description: "",
        subDescription: "",
      };
  }
};

const TimeCalculator = (
  dateValue: string,
  t: TFunction,
  fullText: boolean = false
) => {
  const date = new Date(dateValue);
  const now = new Date();

  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffMinutes === 0) {
    return t("inbox:now");
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}${
      fullText ? " " + t("inbox:minutes_ago") : t("inbox:m")
    }`;
  } else if (diffHours < 24) {
    return `${diffHours}${
      fullText ? " " + t("inbox:hours_ago") : t("inbox:h")
    }`;
  } else {
    return `${diffDays}${fullText ? " " + t("inbox:days_ago") : t("inbox:d")}`;
  }
};

const GetActivityTexts = (
  activity: IInbox,
  type: InboxType,
  t: TFunction,
  inbox: IInbox
) => {
  switch (type) {
    case InboxType.PRODUCT_EXPLORE:
      return {
        title: t("inbox:PRODUCT_EXPLORE", {
          name: TextAllFirstLetterToUpperCase(
            inbox.metadata?.product?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.PRODUCT_COMPARE:
      return {
        title: t("inbox:PRODUCT_COMPARE", {
          name: TextAllFirstLetterToUpperCase(
            inbox.metadata?.product?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_INVITE:
      return {
        title: t("inbox:SPECIFICATION_INVITE", {
          name: activity?.metadata?.sendedBy?.email || "",
        }),
        icon: <Plus className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_CREATED:
      return {
        title: t("inbox:SPECIFICATION_CREATED", {
          name: activity?.metadata?.sendedBy?.email || "",
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_UPDATED:
      return {
        title: t("inbox:SPECIFICATION_UPDATED", {
          name: activity,
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_DELETED:
      return {
        title: t("inbox:SPECIFICATION_DELETED", {
          name: activity,
        }),
        icon: <Trash className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE_STARTED:
      return {
        title: t("inbox:SUPPLIER_ANSWER_DESCRIPTION", {
          productName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.product?.name || ""
          ),
        }),
        icon: <Plus className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE:
      return {
        title: t("inbox:SUPPLIER_ANSWER_DESCRIPTION", {
          productName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.product?.name || ""
          ),
        }),
        icon: <Plus className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SUPPLIER_SPECIFICATION_INVITE_COMPLETED:
      return {
        title: t("inbox:SUPPLIER_ANSWER_DESCRIPTION", {
          productName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.product?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_EXPLORE_MATCHED_PRODUCT_COMPLETED:
      return {
        title: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.specification?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_EXPLORE_COMPETITIVE_INDEX_COMPLETED:
      return {
        title: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.specification?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_EXPLORE_LOCAL_PARTNER_SUPPORT_COMPLETED:
      return {
        title: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.specification?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_EXPLORE_PRODUCER_SCORE_COMPLETED:
      return {
        title: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.specification?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
    case InboxType.SPECIFICATION_EXPLORE_USER_AND_COMMENT_COMPLETED:
      return {
        title: t("inbox:PRODUCT_EXPLORE_DESCRIPTION", {
          specificationName: TextAllFirstLetterToUpperCase(
            activity?.metadata?.specification?.name || ""
          ),
        }),
        icon: <Box className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };

    default:
      return {
        title: "",
        icon: <Plus className="!w-2 !h-2" />,
        date: TimeCalculator(activity.updatedAt || activity.createdAt, t, true),
      };
  }
};

export {
  GetInboxListIcon,
  GetInboxListTexts,
  TimeCalculator,
  GetInboxDisplayTexts,
  GetInboxList,
  GetActivityTexts,
};
